import ObjectCopyService from 'external-services/object-copy-service/index';
import BookingNotificationsService from 'external-services/booking-notification-service/bookingNotifications.service';
import SpamProtectionService from 'external-services/spam-protection-service/SpamProtection.service';
import CustomerUpdateService from 'external-services/customer-update-service/customerUpdate.service';
import StatusConfigService from 'external-services/status-config-service/index';
import NetworkConnectionService from 'external-services/network-connection-service/index';
import AdminService from 'external-services/diary-admin-service/admin.service';
import { cloneDeep } from 'lodash-es';
import moment, { Moment } from 'moment';
import TagsService from 'external-services/tags-service/index';
import {
  BookingStateChangeTypes,
  UnreliableBookingStatusIds,
  BookingStatusNames,
  bookingStatusType,
  BookingType,
  IBooking,
  IBookingStatus,
  IBookingTag,
  ICustomer,
  IDiarySchedule,
  IServiceItem,
  preAuthStatus,
  servicePaymentType,
  StatusNoShow,
  IBookingFromPouch,
  IBookingNotificationEnableStatus,
  AdminServiceLog,
  CustomerTypes,
  ToastType
} from 'shared-types/index';
import BookingChangeService from 'external-services/booking-change-service/bookingChange.service';
import BookingLookupService from 'external-services/booking-lookup-service/index';
import DateUtilsService from 'external-services/date-utils-service/index';
import PouchLoadService from 'external-services/pouch-load-service/index';
import CustomerLookupService from 'external-services/customer-lookup-service/index';
import CustomerNormaliseService from 'external-services/customer-normalise-service/index';
import ServiceDetectionService from 'external-services/service-detection-service/serviceDetection.service';
import BookingLockService from 'external-services/booking-lock-service';
import TagService from 'external-services/tags-service';
import * as BookingTimeChangelog from './bookingTimeChangelog'

const NS = 'BookingUpdateService';

class BookingUpdateService {
  private managePreAuthCallback: (booking: IBooking) => void;
  private showToastCallback: (msg: string, params?: { useBetterToast: boolean, type: ToastType }) => void;

  private trackEvent: (eventName: any, values?: any) => void;
  private track: (name: string, values: [string, any][], customTags?: [string, any][]) => void;
  private handleStatusChange: (cb: (...args: any) => void, actionName?: string) => void;
  /**
   * Diary : current is using App UtilsService validateObject, Should be replace as Shared UtilsService validateBookingObjectByConstraints
   * Diary mobile: Shared UtilsService validateBookingObjectByConstraints
   */
  private customErrorCheck: (booking: IBooking | IBookingFromPouch) => any;

  private getStatusOptions: () => {fullStatusOptions: IBookingStatus[], statusOptions: IBookingStatus[]};

  // @todo WIP - need to finish it and use it
  private isApiCallInProgress: boolean = false;

  // Getter for isApiCallInProgress
  get GetIsApiCallInProgressValue() {
    return this.isApiCallInProgress;
  }

  public setup(
    managePreAuthCallback: (booking: IBooking) => void,
    showToastCallback: (msg: string, options: { useBetterToast: boolean, type: ToastType }) => void,
    trackEvent: (eventName: any, values?: any) => void,
    track: (name: string, values: [string, any][], customTags?: [string, any][]) => void,
    handleStatusChange: (cb: (...args: any) => void, actionName?: string) => void,
    /**
     * Diary : current is using App UtilsService validateObject, Should be replace as Shared UtilsService validateBookingObjectByConstraints
     * Diary mobile: Shared UtilsService validateBookingObjectByConstraints
     */
    customErrorCheck: (booking: IBooking | IBookingFromPouch) => any,
    getStatusOptions: () => {fullStatusOptions: IBookingStatus[], statusOptions: IBookingStatus[]}
  ): void {
    // console.log(NS, 'managePreAuthCallback', managePreAuthCallback);
    // console.log(NS, 'showToastCallback', showToastCallback);
    // console.log(NS, 'trackEvent', trackEvent);
    // console.log(NS, 'track', track);
    // console.log(NS, 'handleStatusChange', handleStatusChange);
    // console.log(NS, 'customErrorCheck', customErrorCheck);

    this.managePreAuthCallback = managePreAuthCallback;
    this.showToastCallback = showToastCallback;

    this.trackEvent = trackEvent;
    this.track = track;
    this.handleStatusChange = handleStatusChange;
    this.customErrorCheck = customErrorCheck;
    this.getStatusOptions = getStatusOptions;
  }
  /**
   * Add a new booking, logic is:
   * 1) Validate the input booking
   * 2) Add/Update the customer
   * 3) Copy customer major info into the booking
   * 4) Existing bookings are updated via Customer update event
   * @param {Object} booking
   * @param {Object} customer - associated to the booking, can be new or existing one
   * @param {Object} [bookingToMove] - original booking
   * @param {Object} [schedule] - The schedule
   * @return {promise}
   */
  public add(
    bookingParam: IBooking,
    customer: ICustomer,
    bookingToMove: any = null,
    schedule: IDiarySchedule = null,
    isRecurringBooking: boolean = false,
    forceUpdateCustomer: boolean = false
  ): Promise<IBookingFromPouch> {
    return new Promise((resolve, reject) => {
      let booking = cloneDeep(bookingParam)
      /**
       * filters out dirty data on booking tags that accidentally
       */
      TagService.filterTagsBeforeSavingToApi(booking)

      // Can not add a booking without a customer associated
      if (!customer) {
        reject('A customer must be passed');
        return;
      }

      console.log('Add - New Booking id', booking._id, 'rev', booking._rev);
      if (bookingToMove) {
        console.log('Add - Original Booking id', bookingToMove._id, 'rev', bookingToMove._rev);
      }

      // Valorize booking required fields
      booking = ObjectCopyService.angularSafeCopy(booking);
      BookingLookupService.scriptInjectionPrevention(booking);

      booking._id = PouchLoadService.getNewId([
        'booking',
        DateUtilsService.dateToIdStr(booking.time),
      ]);
      booking.bookingRef = new Date().getTime().toString().slice(-8); // Unix epoch last 8 digits as string
      this.setServiceDetailsOnBooking(booking, schedule);
      if(!booking.timeChangelog){
        booking.timeChangelog = [];
      }
      BookingTimeChangelog.update(booking.timeChangelog, booking.time);
      const bookingWithStringDates: IBookingFromPouch = booking as unknown as IBookingFromPouch;
      bookingWithStringDates.timeChangelog = booking.timeChangelog.map(x=>BookingTimeChangelog.toPouchEntry(x));
      bookingWithStringDates.bookedAt = new Date().toISOString();
      this.roundDuration(bookingWithStringDates);

      // Guard invalid booking
      const errors: any = this.getBookingErrors(bookingWithStringDates);
      if (errors) {
        console.error('booking failed validation', bookingWithStringDates, errors);
        reject('Add Error: Booking is not valid');
        return;
      }

      const spamBooking: any = ObjectCopyService.angularSafeCopy(bookingWithStringDates); // Actually this is IBooking
      spamBooking.checkCustomer = ObjectCopyService.angularSafeCopy(customer);

      if (SpamProtectionService.isSpamBooking(spamBooking)) {
        console.error('detected auto spam booking', spamBooking);
        reject('Add Error: Detected spam booking');
        return;
      }

      try {
        // Add or Update the customer
        let storeCustomerPromise: Promise<ICustomer> = Promise.resolve(customer);
        if (
          customer.updatingCustomer ||
          (customer.customerType !== CustomerTypes.inline && !bookingToMove) ||
          bookingWithStringDates.bookingType === BookingType.StandbyList
        ) {
          // Add booking tags to the customer tags
          const {customer: _customer, isUpdate} = this.updateCustomerPermanentTags(bookingWithStringDates, customer);
          if (_customer._id) {
            if (isUpdate || forceUpdateCustomer) {
              storeCustomerPromise = CustomerUpdateService.update(_customer);
            }
          } else {
            storeCustomerPromise = CustomerUpdateService.add(_customer);
          }
        }

        storeCustomerPromise
          .then((customer: ICustomer) => {
            CustomerNormaliseService.updateBookingCustomerInfo(bookingWithStringDates, customer);

            bookingWithStringDates.lastModifiedDate = new Date().toISOString();
            bookingWithStringDates.time = DateUtilsService.dateToDateTimeStr(booking.time);
            bookingWithStringDates.orgTime = booking.time.toString(); // Horita: origin code booking.orgTime = booking.time
            // Ensure that people attr is of type number
            bookingWithStringDates.people = parseInt(bookingWithStringDates.people.toString());

            // save the id of the booking this was created from
            if (bookingToMove) {
              bookingWithStringDates.movedFromBookingId = bookingToMove._id;
            }

            // Set the status (@todo: Jim to check logic. severity: low
            const statusOptions = this.getStatusOptions().statusOptions;
            for (let i = statusOptions.length - 1; i >= 0; i--) {
              const status: IBookingStatus = statusOptions[i];
              if (status._id === (bookingWithStringDates as any).statusId) {
                (bookingWithStringDates as any).statusCode = status.code;
                break;
              }
            }

            // Validate primaryTableId
            bookingWithStringDates.primaryTableId =
              BookingLookupService.getPrimaryTableId(bookingWithStringDates);

            // Track booking creation
            this.trackEvent('New Booking', {
              id: bookingWithStringDates._id,
              bookingMethod: bookingWithStringDates.method,
              customerType: customer.customerType,
            });
            const couchLink = PouchLoadService.getDBOnlineReduced();

            if (!NetworkConnectionService.isOnline()) {
              const statusChangeHandlers = this.handleStatusChangeOnline(() => {
                // Track booking changes
                let type: BookingStateChangeTypes = BookingStateChangeTypes.CREATED;
                if (bookingToMove) {
                  type = BookingStateChangeTypes.MOVED;
                  // bookingToMove.time = DateUtilsService.dateToDateTimeStr(bookingToMove.time);
                }

                BookingChangeService.saveBookingChange(
                  type,
                  bookingWithStringDates,
                  bookingToMove,
                  bookingWithStringDates.staffName,
                  null,
                  `${NS}.add`
                );

                /**
                 * Removed BookingNotificationsService.sendNotification
                 * no need to send notification here because it will get sent by
                 * BookingNotificationsService.checkForNotifications when coming back online
                 */
              });

              // Save the booking
              PouchLoadService.selectDatabase(bookingWithStringDates.time)
                .put(bookingWithStringDates)
                .then((res: any) => {
                  // Update booking revision
                  bookingWithStringDates._rev = res.rev;

                  // Track booking save
                  this.track(
                    'bookingPoint',
                    [
                      ['booking', 1],
                      ['rev', bookingWithStringDates._rev],
                    ],
                    [
                      ['id', bookingWithStringDates.id],
                      ['status', 'create'],
                    ]
                  );

                  // can't send notification because we're offline, so we save it to pouch to be sent once online again
                  BookingNotificationsService.saveNotification(
                    bookingWithStringDates._id,
                    BookingNotificationsService.NEW,
                    false,
                    AdminService.getVenue().id
                  );
                  statusChangeHandlers.start();

                  // Resolve
                  resolve(bookingWithStringDates);
                })
                .catch((err: any) => {
                  console.error('failed to save', err);
                  statusChangeHandlers.stop();
                  reject(err);
                });
            } else {
              // Save the booking
              // We are online so do direct to reduced but not via pouch.  // never a time wher it cant be in reduced really?
              // we want to go into reduced direct.
              couchLink
                .put(bookingWithStringDates)
                .then(async (res: { id: string; ok: boolean; rev: string }) => {
                  // Update booking revision
                  bookingWithStringDates._rev = res.rev;

                  // Track booking save
                  this.track(
                    'bookingPoint',
                    [
                      ['booking', 1],
                      ['rev', bookingWithStringDates._rev],
                    ],
                    [
                      ['id', bookingWithStringDates.id],
                      ['status', 'create'],
                    ]
                  );

                  // Track booking changes
                  let type: BookingStateChangeTypes = BookingStateChangeTypes.CREATED;
                  if (bookingToMove) {
                    type = BookingStateChangeTypes.MOVED;
                    // bookingToMove.time = DateUtilsService.dateToDateTimeStr(bookingToMove.time);
                  }

                  await this.saveNewBooking(type, bookingWithStringDates, bookingToMove)

                  // If Recurring Booking we dont need to send normal notification - Recurring notification will be sent - Recurring Booking API
                  if (!isRecurringBooking) {
                    // Notify after the booking changes are saved
                    BookingNotificationsService.sendNotification(
                      bookingWithStringDates,
                      BookingNotificationsService.NEW
                    );
                  }

                  // Resolve
                  resolve(bookingWithStringDates);
                })
                .catch((err: any) => {
                  console.error('failed to save', err);
                  reject(err);
                });
            }
          })
          .catch((err) => {
            console.error('AddBooking, failed to store customer', err);
            reject('Failed to save customer');
          });
      } catch (err) {
        console.error('failed to create booking', err);
        reject(err);
      }
    });
  }

  /**
   * Update a booking
   * @param  {Object} booking 84666540
   * @return {promise}
   */
  public updateFromAnywhere(
    bookingParam: IBooking,
    orgBookingParam: IBooking,
    bookingCustomer: ICustomer = null,
    notify: IBookingNotificationEnableStatus | boolean = false,
    staff: string = null,
    schedule: IDiarySchedule = null,
    changesType: BookingStateChangeTypes = null,
    dontChangeLastModifiedDate: any = false,
    forceUpdateCustomer: boolean = false
  ): Promise<IBooking> {
    console.log('updateFromAnywhere', orgBookingParam._rev);

    let smsNotify: boolean = false;
    let emailNotify: boolean = false;

    if (typeof notify === 'object' && notify !== null) {
      emailNotify = notify.emailNotify;
      smsNotify = notify.smsNotify;
    } else {
      emailNotify = !!notify;
      smsNotify = false;
    }

    let booking = cloneDeep(bookingParam)
    const orgBooking = cloneDeep(orgBookingParam)

    /**
     * UI-784: 'TagService.filterTagsBeforeSavingToApi' filters out dirty data on booking tags that accidentally
     * got saved during Diary Mobile pilot phase. Would only affect 2 venues and can be removed Jan 2024.
     */
    TagService.filterTagsBeforeSavingToApi(booking)
    TagService.filterTagsBeforeSavingToApi(orgBooking)

    const bookingLockResult = BookingLockService.checkLockedPendingPayment(orgBooking);

    if (AdminService.isPartnerBookingsLocked(orgBooking?.partner) === true) {
      this.showToastCallback('Booking is locked. Please make changes in Function Tracker.');
      return false as any;
    } else if (bookingLockResult.isLocked) {
      this.showToastCallback(bookingLockResult.reason, { useBetterToast: true, type: ToastType.error});
      return false as any;
    } else {
      const { tempBooking, tempOrgBooking } = this.generateTempBookings(booking, orgBooking);

      const { isNoShow, wasNoShow, mustUpdateCustomer } = this.getNoShowVars(booking, orgBooking);

      return new Promise((resolve, reject) => {
        // Guard booking validity
        const errors = this.getBookingErrors(booking);
        if (errors) {
          console.error('booking', booking, errors);
          reject('Add Error: Booking is not valid');
          return;
        }

        this.getCustomer(booking, bookingCustomer, mustUpdateCustomer)
          .then((customer: ICustomer) => {
            booking = this.getClonedBookingWithValidatedProperties(booking, schedule);

            this.setServiceDetailsOnBooking(booking, schedule);

            // Add or Update the customer
            // @todo: Make it smarter and update the customer only if something change. severity: low
            let storeCustomerPromise: Promise<ICustomer> = Promise.resolve(customer);
            if (customer && customer.customerType !== CustomerTypes.inline) {
              this.updateCustomerNoShowCount(customer, isNoShow, wasNoShow);
              const {customer: _customer, isUpdate} = this.updateCustomerPermanentTags(booking, customer);
              if (_customer._id) {
                if (isUpdate || forceUpdateCustomer) {
                  storeCustomerPromise = CustomerUpdateService.update(_customer);
                }
              } else {
                storeCustomerPromise = CustomerUpdateService.add(_customer);
              }
            }

            // Store customer
            storeCustomerPromise
              .then((customer: ICustomer) => {
                CustomerNormaliseService.updateBookingCustomerInfo(booking, customer);
                this.updateBookingPermanentTags(booking, customer);

                // Tracking
                this.trackEvent('Edit Booking', {
                  id: booking._id,
                  rev: booking._rev,
                });

                // added a WaitList check here, since it was failing for walkins
                if (
                  tempBooking.bookingType !== BookingType.WaitList &&
                  ObjectCopyService.angularSafeJson(tempBooking) ===
                    ObjectCopyService.angularSafeJson(tempOrgBooking)
                ) {
                  console.log('True');
                  resolve(booking);
                  return;
                }
                // Otherwise cary on as per normal
                else {
                  this.updateInCouchOrPouchWhenOffline(booking, dontChangeLastModifiedDate)
                    .then(
                      (res: {
                        id: string; //booking_2022-11-01_O7TS8KBL9TBGU_1667270231793
                        ok: boolean; // true
                        rev: string; //5-65bb4b81fc609b7219168a32b3101cc3
                      }) => {
                        console.log('first notify ' + notify);

                        // Track booking changes
                        if (!changesType) {
                          changesType = BookingStateChangeTypes.UPDATED;
                        }

                        // NBI-4786 - issue where the correct format of time not being sent to API - We are doing the same in updateInCouchOrPouchWhenOffline above - need to check how it used to happen earlier
                        const bookingWithStringDates: IBookingFromPouch =
                          ObjectCopyService.angularSafeCopy(booking);
                        bookingWithStringDates.time = DateUtilsService.dateToDateTimeStr(
                          booking.time
                        );
                        bookingWithStringDates.timeChangelog = booking.timeChangelog?.map(x=>(
                          { version: x.version,
                            time: DateUtilsService.dateToDateTimeStr(x.time)
                          }));
                        this.roundDuration(bookingWithStringDates);
                        BookingChangeService.saveBookingChange(
                          changesType,
                          bookingWithStringDates,
                          orgBooking,
                          staff,
                          null,
                          `${NS}.updateFromAnywhere`
                        ).then(() => {
                          // Notify booking update after booking Changes are saved
                          const modifiedProperties: string = this.findModifiedProperties(
                            orgBooking,
                            booking
                          );
                          BookingNotificationsService.sendNotification(
                            bookingWithStringDates,
                            BookingNotificationsService.EDIT,
                            emailNotify,
                            smsNotify,
                            null,
                            null,
                            modifiedProperties
                          );
                        });

                        // see if the booking was cancelled and needs a pre auth.
                        this.displayManagePreAuthDialog(booking, orgBooking);

                        booking._rev = res.rev;
                        resolve(booking);
                      }
                    )
                    .catch((err: any) => {
                      reject(err);
                    }); // save booking
                }
              })
              .catch((err: any) => {
                reject(err);
              }); // save customer
          })
          .catch((err: any) => {
            reject(err);
          }); // get customer
      }); // $q
    }
  } // update booking

  /**
   * THis is a similar method, but it will only be called when updating from the edit screen first page.
   * where as all the other once could be updated from everywhere.
   * @param  {Object} booking
   * @return {promise}
   */
  public updateFromEditBookingSave(
    bookingParam: IBooking,
    orgBookingParam: IBooking,
    bookingCustomer: ICustomer,
    orgCustomer: ICustomer,
    emailNotify: boolean,
    smsNotify: boolean,
    staff: string,
    schedule: IDiarySchedule,
    changesType?: BookingStateChangeTypes,
    dontChangeLastModifiedDate = false,
    forceUpdateCustomer: boolean = false
  ): Promise<IBooking> {
    let booking = cloneDeep(bookingParam)
    const orgBooking = cloneDeep(orgBookingParam)

    /**
     * filters out dirty data on booking tags that accidentally
     */
    TagService.filterTagsBeforeSavingToApi(booking)
    TagService.filterTagsBeforeSavingToApi(orgBooking)

    if (booking.isBookedBy) {
      TagService.addBookedByTag(booking.tags);
    }
    else {
      TagService.removeBookedByTag(booking.tags)
    }

    console.log('updateFromEditBookingSave');
    const { tempBooking, tempOrgBooking } = this.generateTempBookings(booking, orgBooking);

    const { isNoShow, wasNoShow, mustUpdateCustomer } = this.getNoShowVars(booking, orgBooking);

    this.verifyBookingEndDate(booking, schedule);

    return new Promise((resolve, reject) => {
      // Guard booking validity
      const errors: any = this.getBookingErrors(booking);
      if (errors) {
        console.error('booking', booking, errors);
        reject('Add Error: Booking is not valid');
        return;
      }

      const updateCustomerAPI: boolean = this.checkIfCustomerAPINeedsUpdating(
        booking,
        orgBooking,
        bookingCustomer,
        orgCustomer,
        tempBooking,
        tempOrgBooking
      );

      // we want to check that the booking hasnt been edited, and that customr dosnt need to be updated, if thats the case we suppress the saves.
      // This will fix pre auths being update in the database via the exte
      if ((ObjectCopyService.angularSafeJson(tempBooking) === ObjectCopyService.angularSafeJson(tempOrgBooking)
        && updateCustomerAPI == false) && (!emailNotify && !smsNotify)) {
        console.log('Skip Saving');
        resolve(booking);
        return;
      }
      // Otherwise cary on as per normal
      else {
        console.log('Perform Saving');

        this.getCustomer(booking, bookingCustomer, mustUpdateCustomer)
          .then((customer: ICustomer) => {
            booking = this.getClonedBookingWithValidatedProperties(booking, schedule);

            this.setServiceDetailsOnBooking(booking, schedule);

            // Add or Update the customer
            // TODO Make it smarter and update the customer only if something change
            if (customer && customer.customerType !== CustomerTypes.inline) {
              this.updateCustomerNoShowCount(customer, isNoShow, wasNoShow);
              const res = this.updateCustomerPermanentTags(booking, customer);
              customer = res.customer;
              if (customer._id) {
                if (updateCustomerAPI || res.isUpdate || forceUpdateCustomer) {
                  CustomerUpdateService.update(customer);
                }
              } else {
                CustomerUpdateService.add(customer);
              }
            }

            // Store customer
            // storeCustomerPromise.then(customer => {

            CustomerNormaliseService.updateBookingCustomerInfo(booking, customer);
            this.updateBookingPermanentTags(booking, customer);

            // Tracking
            this.trackEvent('Edit Booking', {
              id: booking._id,
              rev: booking._rev,
            });

            if (
              ObjectCopyService.angularSafeJson(tempBooking) ===
                ObjectCopyService.angularSafeJson(tempOrgBooking) &&
              updateCustomerAPI == false && (!emailNotify && !smsNotify)
            ) {
              console.log('True');
              resolve(booking);
              return;
            }

            BookingLookupService.scriptInjectionPrevention(booking);
            this.updateInCouchOrPouchWhenOffline(booking, dontChangeLastModifiedDate)
              .then((res: { id: string; ok: boolean; rev: string }) => {
                // Track booking changes
                if (!changesType) {
                  changesType = BookingStateChangeTypes.UPDATED;
                }

                // adding server logging to capture evidence when tables = null - NBI-3868
                // the condition [tables: null] triggers an exception on the backend side that affects the Widget loading
                // However, bookings with BookingType.StandbyList or BookingType.WaitList are able to save with empty tables (BUT NOT NULL).
                //
                // tables: []    => EXCEPTION FREE
                // tables: null  => TRIGGERS EXCEPTION ON API

                this.addNullTablesLogging(booking);

                // NBI-4786 - issue where the correct format of time not being sent to API - We are doing the same in updateInCouchOrPouchWhenOffline above - need to check how it used to happen earlier
                const bookingWithStringDates: IBookingFromPouch =
                  ObjectCopyService.angularSafeCopy(booking);
                bookingWithStringDates.time = DateUtilsService.dateToDateTimeStr(booking.time);
                bookingWithStringDates.timeChangelog = booking.timeChangelog?.map(x=>(
                      { version: x.version,
                        time: DateUtilsService.dateToDateTimeStr(x.time)
                      }));
                this.roundDuration(bookingWithStringDates);

                // Save booking changes
                BookingChangeService.saveBookingChange(
                  changesType,
                  bookingWithStringDates,
                  orgBooking,
                  staff,
                  null,
                  `${NS}.updateFromEditBookingSave`
                ).then(() => {
                  // Notify booking update after booking Changes are saved
                  const modifiedProperties: string = this.findModifiedProperties(
                    orgBooking,
                    booking
                  );
                  BookingNotificationsService.sendNotification(
                    bookingWithStringDates,
                    BookingNotificationsService.EDIT,
                    emailNotify,
                    smsNotify,
                    null,
                    null,
                    modifiedProperties
                  );
                });

                // see if the booking was cancelled and needs a pre auth.
                this.displayManagePreAuthDialog(booking, orgBooking);

                booking._rev = res.rev;
                resolve(booking);
              })
              .catch((err: any) => reject(err)); // save booking
          })
          .catch((err: any) => reject(err)); // get customer
      } // end inf booking changes
    }); // $q

    // update booking
  }

  /**
   * Delete a booking
   * @param  {Object} booking
   * @return {promise}
   */
  public setToCancelled(
    booking: IBooking,
    staff: string = null,
    changesType: BookingStateChangeTypes = null,
    getCancelledStatus: () => IBookingStatus = () => {
      const fullStatusOptions = this.getStatusOptions().fullStatusOptions;
      return StatusConfigService.getStatusByName('Cancelled', fullStatusOptions);
    }
  ): Promise<IBooking> {
    const orgBooking: IBooking = ObjectCopyService.angularSafeCopy(booking);
    booking.status = getCancelledStatus();
    booking = this.updateAfterStatusChange(booking, orgBooking);

    console.warn('Cancelling booking', booking._id, booking._rev, booking.time);

    return this.updateFromAnywhere(booking, orgBooking, null, null, staff, null, changesType);
  }

  public updateAfterStatusChange(booking: IBooking, orgBooking: IBooking): IBooking {
    const bookingStartTime: Date = cloneDeep(booking.time);
    const bookingEndTime: Date = cloneDeep(booking.bookingEnd);
    const venueTime: Date = DateUtilsService.getVenueTime(AdminService.getVenueTimezoneId());

    if (booking.status.statusType === bookingStatusType.finished) {
      if (
        bookingStartTime.getTime() < venueTime.getTime() &&
        venueTime.getTime() < bookingEndTime.getTime()
      ) {
        // update the booking duration time
        let newDuration: number = Math.ceil(
          DateUtilsService.minsBetweenDates(bookingStartTime, venueTime)
        );
        newDuration = newDuration < 1 ? 1 : newDuration;
        booking.duration = newDuration;
      }
    }

    // the booking has been changed to seated.
    if (
      booking.status.statusType === bookingStatusType.seated &&
      orgBooking.status.statusType !== bookingStatusType.seated
    ) {
      this.updateTimesOnBookingWhenSeated(booking, bookingStartTime);
    }

    return booking;
  }

  public updateTimesOnBookingWhenSeated(booking: IBooking, originalBookingStartTime: Date) {
    const venueTime: Date = DateUtilsService.getVenueTime(AdminService.getVenueTimezoneId());

    const bufferTime = 30 * 60 * 1000; // 30 minutes
    const timeDiff: number = venueTime.getTime() - originalBookingStartTime.getTime();
    
    // the start time is within the 30mins after the original booking's start time
    const isWithinBuffer = timeDiff < 0 && timeDiff > -bufferTime;
    
    if (isWithinBuffer) {
      const newBookingTime: Date = cloneDeep(venueTime);
      newBookingTime.setSeconds(0);
      newBookingTime.setMilliseconds(0);
      booking.time = newBookingTime;
      
      let duration: number = typeof booking.duration === 'string' ? parseFloat(booking.duration) : booking.duration;
      const extraDuration = Math.ceil(Math.abs(timeDiff / 1000 / 60));
      duration += extraDuration;
      booking.duration = duration;

      // adds the duration to the booking's end time
      booking.bookingEnd = DateUtilsService.addMinutes(newBookingTime, duration);
    }
  }

  public updateInCouchOrPouchWhenOffline(
    booking: IBooking,
    dontChangeLastModifiedDate: any
  ): Promise<{
    id: string; //booking_2022-11-01_O7TS8KBL9TBGU_1667270231793
    ok: boolean; // true
    rev: string; //5-65bb4b81fc609b7219168a32b3101cc3
  }> {
    let databaseRef;
    if (!NetworkConnectionService.isOnline()) {
      console.debug('Saving Booking to pouchDb - offline');
      databaseRef = PouchLoadService.selectDatabase(booking.time);
    } else {
      console.debug('Saving Booking to couchDb - online');
      databaseRef = PouchLoadService.selectOnlineDatabaseAsFirstOption(booking.time);
    }
    if(!booking.timeChangelog){
      booking.timeChangelog = []; //This could happen if a booking was created before timeChangelog was supported.
    }
     //Update booking changelog
    BookingTimeChangelog.update(booking.timeChangelog, booking.time);

    const bookingWithStringDates: IBookingFromPouch = ObjectCopyService.angularSafeCopy(booking);
    bookingWithStringDates.time = DateUtilsService.dateToDateTimeStr(booking.time);
    bookingWithStringDates.timeChangelog = booking.timeChangelog.map(x=>BookingTimeChangelog.toPouchEntry(x));
    if (!dontChangeLastModifiedDate) {
      bookingWithStringDates.lastModifiedDate = new Date().toISOString();
    }
    bookingWithStringDates.people = parseInt(bookingWithStringDates.people.toString());
    this.roundDuration(bookingWithStringDates);
    delete bookingWithStringDates.isSaving;
    delete bookingWithStringDates.overdue;

    return databaseRef.put(bookingWithStringDates);
  }

  public updateBookingPermanentTags(booking: IBooking, customer: ICustomer): void {
    if (booking && customer) {
      for (const customerTagIndex in customer.tags) {
        const bookingTagIndex: number = booking.tags.findIndex(
          (tag: IBookingTag) => tag._id === customer.tags[customerTagIndex]._id
        );
        if (bookingTagIndex === -1 && customer.tags[customerTagIndex].permanent) {
          // add permanent tags that are in customer objects but not in bookings object
          booking.tags.push(customer.tags[customerTagIndex]);
        } else {
          // update permanent tag that is both in customer and booking object
          booking.tags[bookingTagIndex] = customer.tags[customerTagIndex];
        }
      }
      // remove permanet tags that are in booking object but not in customer object
      for (let i = booking.tags.length - 1; i >= 0; i--) {
        if (
          booking.tags[i] &&
          booking.tags[i].permanent &&
          !customer?.tags?.find((tag: IBookingTag) => tag._id === booking.tags[i]._id)
        ) {
          booking.tags.splice(i, 1);
        }
      }
    }
  }

  public updateCustomerPermanentTags(
    booking: IBooking | IBookingFromPouch,
    customer: ICustomer
  ): {customer: ICustomer, isUpdate: boolean} {
    let isUpdate = false;
    if (!booking.tags || !customer || customer.customerType === CustomerTypes.inline) {
      return {customer, isUpdate};
    }

    if (!customer.tags) {
      customer.tags = [];
    }

    // Difference track
    let toAdd: IBookingTag[] = [],
      toRemove: IBookingTag[] = [];

    // All permanent booking tags are possible adds
    toAdd = toAdd.concat(booking.tags.filter((tag: IBookingTag) => tag.permanent));

    // All customer tags not in booking are possible removes
    toRemove = toRemove.concat(this.filterCustomerTagByBooking(booking, customer));

    // Add and remove tags on Customer
    toAdd.forEach((tagToAdd: IBookingTag) => {
      if (
        customer.tags.find((customerTag: IBookingTag) => customerTag._id === tagToAdd._id) == null
      ) {
        isUpdate = true;
        customer.tags.push(tagToAdd);
      }
      // if no show tag is being added and no show is 0, bump to 1
      if (tagToAdd._id === TagsService.noShowTag._id && customer.noShowCount === 0) {
        customer.noShowCount = 1;
      }
    });
    toRemove.forEach((tagToRemove: IBookingTag) => {
      const index: number = customer.tags.findIndex(
        (customerTag: IBookingTag) => customerTag._id === tagToRemove._id
      );
      if (index > -1) {
        // if no show tag is being removed, reset noShowCount to 0
        if (customer.tags[index]._id === TagsService.noShowTag._id) {
          customer.noShowCount = 0;
        }
        isUpdate = true;
        customer.tags.splice(index, 1);
      }
    });

    // Check noShowTag status
    if (
      customer.noShowCount &&
      customer.noShowCount > 0 &&
      !customer.tags.find((tag: IBookingTag) => tag._id === TagsService.noShowTag._id)
    ) {
      if (!AdminService.getVenue().isOnlineCustomersEnabled) {
        customer.tags.push(TagsService.noShowTag);
      } else {
        customer.tags.push(TagsService.nowShowTagShared);
      }
      isUpdate = true;
    }

    return {customer, isUpdate};
  }

  /**
   * This is only for unit testing
   */
  public async _unitTestSaveNewBooking(
    type: BookingStateChangeTypes,
    booking: IBooking | IBookingFromPouch,
    bookingToMove: IBooking
  ): Promise<void> {
    return await this.saveNewBooking(type, booking, bookingToMove);
  }
  /**
   * Calls the save booking api only once in 2 seconds
   */
  private async saveNewBooking(
    type: BookingStateChangeTypes,
    booking: IBooking | IBookingFromPouch,
    bookingToMove: IBooking
  ): Promise<void> {
    if (!this.isApiCallInProgress) {
      this.isApiCallInProgress = true;
      try {
        await BookingChangeService.saveBookingChange(
          type,
          booking,
          bookingToMove,
          booking.staffName,
          null,
          `${NS}.add -> saveNewBooking`
        );
      } finally {
        this.isApiCallInProgress = false;
      }
    }
  }

  private setServiceDetailsOnBooking(booking: IBooking, schedule: IDiarySchedule): void {
    // find matched service after serviceId override
    if (schedule) {
      const matchedService: IServiceItem = ServiceDetectionService.getBookingService(
        booking,
        schedule
      );
      if (matchedService) {
        booking.serviceName = matchedService.name;
        booking.serviceType = matchedService.type;
      }
    }
  }

  private roundDuration(booking: IBookingFromPouch): void {
    const duration: number = Math.round(Number(booking.duration));
    if (!isNaN(duration)) {
      booking.duration = duration;
    }
  }

  private getBookingErrors(booking: IBooking | IBookingFromPouch) {
    return this.customErrorCheck(booking);
  }

  private verifyBookingEndDate(booking: IBooking, schedule: IDiarySchedule): void {
    if (booking.bookingEnd.toString() == 'Invalid Date') {
      const targetService: IServiceItem = schedule.services.find(
        (svc: IServiceItem) => svc._id == booking.serviceId
      );
      const duration: number =
        Number(booking.duration) || (targetService ? targetService.bookingDuration : 0);

      if (duration) {
        const bookingEnd: Moment = moment(booking.time).add(duration, 'minutes');
        booking.bookingEnd = bookingEnd.toDate();
        booking.duration = duration;
      }
    }
  }

  private findModifiedProperties(
    originalBooking: IBooking | IBookingFromPouch,
    modifiedBooking: IBooking
  ): string {
    let modifiedProperties: string = '';

    if (originalBooking.status.name !== modifiedBooking.status.name) {
      modifiedProperties = 'status';
    }

    if (originalBooking.people !== modifiedBooking.people) {
      modifiedProperties += ',people';
    }

    const arr1: string = JSON.stringify(originalBooking.tables);
    const arr2: string = JSON.stringify(modifiedBooking.tables);
    if (arr1 !== arr2) {
      modifiedProperties += ',table';
    }

    //We compare length of the changelog, rather than time here, to avoid the whole mess of the original
    //bookings times potentially being strings.
    if(originalBooking.timeChangelog?.length !== modifiedBooking.timeChangelog?.length){
      modifiedProperties += ',time';
    }

    return modifiedProperties;
  }

  private getCustomer(
    booking: IBooking,
    customer: ICustomer,
    mustUpdateCustomer: boolean
  ): Promise<ICustomer> {
    return new Promise((resolve) => {
      // we have it already, return it easy
      if (customer) {
        resolve(customer);
        return;
      }
      // we do not need the customer for this change
      if (!mustUpdateCustomer) {
        resolve(null);
        return;
      }
      // get the customer we need to make change to it
      CustomerLookupService.getById(booking.customerId)
        .then((customer: ICustomer) => resolve(customer))
        .catch(() => resolve(null));
    });
  }

  /**
   * Listens for first status change to be triggered once back online
   * @param onlineHandler - a callback to call once back online
   * @returns start function and stop (for cleaning up listeners).
   */
  private handleStatusChangeOnline(onlineHandler: () => void): {
    start: () => void;
    stop: () => void;
  } {
    let statusChangeUnlistener: () => void;
    const statusChangeTO: any[] = [];

    const stop = () => {
      if (statusChangeUnlistener) {
        statusChangeUnlistener();
      }
      if (statusChangeTO.length) {
        statusChangeTO.forEach((to) => clearTimeout(to));
      }
    };

    const start = () => {
      statusChangeUnlistener = this.handleStatusChange(() => {
        statusChangeTO.push(
          setTimeout(() => {
            if (NetworkConnectionService.isOnline()) {
              stop();
              onlineHandler();
            }
          }, 1000) // timeout just gives couch and api a chance to get their status updated too
        );
      }) as any;
    };

    return {
      start,
      stop,
    };
  }

  // just for unit tests
  public _test_handleStatusChangeOnline(onlineHandler: () => void): {
    start: () => void;
    stop: () => void;
  } {
    return this.handleStatusChangeOnline(onlineHandler);
  }

  private isExistedTagById(customerTag: IBookingTag, booking: IBooking | IBookingFromPouch) {
    return booking.tags.find((bookingTag: IBookingTag) => customerTag._id === bookingTag._id);
  }

  private filterCustomerTagByBooking(
    booking: IBooking | IBookingFromPouch,
    customer: ICustomer
  ): IBookingTag[] {
    return customer.tags.filter(
      (customerTag: IBookingTag) => !this.isExistedTagById(customerTag, booking)
    );
  }
  //---------------------NEW---------------------

  private generateTempBookings(
    booking: IBooking,
    orgBooking: IBooking
  ): { tempBooking: IBooking; tempOrgBooking: IBooking } {
    const tempBooking: IBooking = ObjectCopyService.angularSafeCopy(booking);
    const tempOrgBooking: IBooking = ObjectCopyService.angularSafeCopy(orgBooking);

    delete tempOrgBooking['_rev'];
    delete tempBooking['_rev'];

    delete tempOrgBooking['serviceIdOverRide'];
    delete tempBooking['serviceIdOverRide'];
    return { tempBooking, tempOrgBooking };
  }

  private getNoShowVars(
    booking: IBooking,
    orgBooking: IBooking
  ): { isNoShow: boolean; wasNoShow: boolean; mustUpdateCustomer: boolean } {
    const newBookingIsNoShow: boolean =
      booking.status.name.toLowerCase() === BookingStatusNames.CancelledNoShow.toLowerCase() ||
      booking.status._id === UnreliableBookingStatusIds.StatusCancelledNoShow;
    const originalBookingIsNoShow: boolean =
      orgBooking.status.name.toLowerCase() === BookingStatusNames.CancelledNoShow.toLowerCase() ||
      orgBooking.status._id === UnreliableBookingStatusIds.StatusCancelledNoShow;

    const isNoShow: boolean = newBookingIsNoShow && !originalBookingIsNoShow;
    const wasNoShow: boolean = !newBookingIsNoShow && originalBookingIsNoShow;

    const mustUpdateCustomer: boolean = isNoShow || wasNoShow;
    return { isNoShow, wasNoShow, mustUpdateCustomer };
  }

  private checkIfCustomerAPINeedsUpdating(
    booking: IBooking,
    orgBooking: IBooking,
    bookingCustomer: ICustomer,
    orgCustomer: ICustomer,
    tempBooking: IBooking,
    tempOrgBooking: IBooking
  ): boolean {
    const debugForTesting = (updateCustomerAPI: boolean) => {
      // Debug for testing
      this.debugForTesting(booking, orgBooking, bookingCustomer, orgCustomer, updateCustomerAPI);
      console.log(ObjectCopyService.angularSafeJson(tempBooking));
      console.log(ObjectCopyService.angularSafeJson(tempOrgBooking));
      console.log(updateCustomerAPI);
    };

    // check to see if tags, notes, or status' have changed to no show.
    // Because if they have we need to update the customer object otherwise not.
    // This saves a lot of updates called in the DB.

    const bothCustomersEmpty = bookingCustomer == null && orgCustomer == null;
    const isQuickStatusUpdateOnly =
      bothCustomersEmpty || bookingCustomer == null || typeof orgCustomer === 'undefined';

    if (!isQuickStatusUpdateOnly) {
      // If customer notes are different, then update as them restored on the actual customer object.
      if (
        bookingCustomer?.notes &&
        orgCustomer?.notes &&
        bookingCustomer?.notes !== orgCustomer?.notes
      ) {
        debugForTesting(true);
        return true;
      }

      // Tags have changed and are per customer so update also.
      if (
        ObjectCopyService.angularSafeJson(tempBooking.tags) !==
        ObjectCopyService.angularSafeJson(tempOrgBooking.tags)
      ) {
        debugForTesting(true);
        return true;
      }

      // now check if customer is different
      if (
        ObjectCopyService.angularSafeJson(bookingCustomer) !==
        ObjectCopyService.angularSafeJson(orgCustomer)
      ) {
        debugForTesting(true);
        return true;
      }

      // now check if status is different
      if (
        orgBooking.status.code !== booking.status.code &&
        (orgBooking.status.code === StatusNoShow.code || booking.status.code === StatusNoShow.code)
      ) {
        debugForTesting(true);
        return true;
      }

      // Debug for testing
      debugForTesting(false);
    }
    return false;
  }

  private getClonedBookingWithValidatedProperties(
    booking: IBooking,
    schedule: IDiarySchedule = null
  ): IBooking {
    const bookingCopy = ObjectCopyService.angularSafeCopy(booking);

    // Validate primaryTableId
    bookingCopy.primaryTableId = BookingLookupService.getPrimaryTableId(booking);

    // Make sure the bookings is in the correct service
    if (schedule) {
      const matchedServiceId: string = ServiceDetectionService.bookingInService(booking, schedule);
      bookingCopy.serviceId = matchedServiceId || bookingCopy.serviceId;
    }

    if (bookingCopy.serviceIdOverRide) {
      bookingCopy.serviceId = bookingCopy.serviceIdOverRide;
    }
    bookingCopy.serviceIdOverRide = '';
    return bookingCopy;
  }

  private updateCustomerNoShowCount(
    customer: ICustomer,
    isNoShow: boolean,
    wasNoShow: boolean
  ): void {
    customer.noShowCount = customer.noShowCount || 0;
    if (isNoShow) {
      // the book is now a no show add to the count
      customer.noShowCount++;
    }
    if (wasNoShow) {
      // the book was a no show take the no show count down
      customer.noShowCount--;
    }
  }

  private addNullTablesLogging(booking: IBooking): void {
    if (!booking.tables) {
      console.log('WARNING - DETECTED NULL TABLES ON BOOKING');
      const notes: string = `Booking Data: {"_id": "${booking._id}",
                "_rev": "${booking._rev}", "bookingType": "${booking.bookingType}", "tables": "${booking.tables}"}`;
      AdminService.serverLoggingDbEvents(
        AdminService.getVenue().id.toString(),
        'Updated Booking with NULL tables.',
        AdminServiceLog.ERRORLOG,
        notes
      );
    }
  }

  private displayManagePreAuthDialog(booking: IBooking, orgBooking: IBooking): void {
    // see if the booking was cancelled and needs a pre auth.
    if (
      booking.paymentSummary &&
      booking.paymentSummary.paymentType === servicePaymentType.preAuth &&
      booking.paymentSummary.preAuthStatus === preAuthStatus.holding &&
      booking.status.statusType === bookingStatusType.cancelled &&
      orgBooking.status.statusType !== bookingStatusType.cancelled
    ) {
      this.managePreAuthCallback(booking);
    }
  }

  private debugForTesting(
    booking: IBooking,
    orgBooking: IBooking,
    bookingCustomer: ICustomer,
    orgCustomer: ICustomer,
    updateCustomerAPI: boolean
  ): void {
    return; // was causing JS error, so blocked it
    if (
      window.location.href.indexOf('restaurant-') > 0 ||
      window.location.href.indexOf('localhost') > 0
    ) {
      console.log('Start check : ');
      console.log(
        'tags different old vs new: ' +
          ObjectCopyService.angularSafeJson('booking.tags') +
          '  vs ' +
          ObjectCopyService.angularSafeJson('orgBooking.tags') +
          ' are diff :  ' +
          (ObjectCopyService.angularSafeJson(booking.tags) !==
            ObjectCopyService.angularSafeJson(orgBooking.tags))
      );
      console.log(
        'notes different old vs new: ' +
          (bookingCustomer.notes ? bookingCustomer.notes : '') +
          '  vs ' +
          (orgCustomer.notes ? orgCustomer.notes : '') +
          ' are diff :  ' +
          (orgCustomer.notes && bookingCustomer.notes
            ? bookingCustomer.notes !== orgCustomer.notes
            : 'no notes walkin')
      );
      console.log(
        'bookingCustomer different old vs new: ' +
          ObjectCopyService.angularSafeJson('bookingCustomer') +
          '  vs ' +
          ObjectCopyService.angularSafeJson('orgCustomer') +
          ' are diff :  ' +
          (ObjectCopyService.angularSafeJson(bookingCustomer) !==
            ObjectCopyService.angularSafeJson(orgCustomer))
      );
      console.log(
        'status.code  different old vs new: ' +
          orgBooking.status.code +
          '  vs ' +
          booking.status.code +
          ' are diff :  ' +
          (orgBooking.status.code !== booking.status.code)
      );
      console.log(
        'orgBooking.status.code vs StatusNoShow.code: ' +
          orgBooking.status.code +
          '  vs ' +
          StatusNoShow.code +
          ' are diff :  ' +
          (orgBooking.status.code === StatusNoShow.code)
      );
      console.log(
        'orgBooking.status.code vs  booking.status.code: ' +
          orgBooking.status.code +
          '  vs ' +
          booking.status.code +
          ' are diff :  ' +
          (orgBooking.status.code !== booking.status.code)
      );
      console.log(
        'orgBooking.status.code vs   StatusNoShow.code: .' +
          orgBooking.status.code +
          '.  vs .' +
          StatusNoShow.code +
          '. are diff :  ' +
          (orgBooking.status.code === StatusNoShow.code)
      );
      console.log(
        'booking.status.code vs StatusNoShow.code: ' +
          booking.status.code +
          '  vs ' +
          StatusNoShow.code +
          ' are diff :  ' +
          (booking.status.code === StatusNoShow.code)
      );
      console.log(' HIT DB FOR UPDATE CUSTOMER IN DB? : ' + updateCustomerAPI);
      console.log('End check');
    }
  }
}

const instance = new BookingUpdateService();
export default instance as BookingUpdateService;
